import { useState, useRef } from 'react'
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'

const ImageUpload = ({
    value,
    onChange,
    placeholder = "Click to upload image",
    accept = "image/*",
    maxSize = 5 * 1024 * 1024, // 5MB
    className = "",
    disabled = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false)
    const [error, setError] = useState('')
    const fileInputRef = useRef(null)

    const handleFileSelect = (file) => {
        setError('')

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file')
            return
        }

        // Validate file size
        if (file.size > maxSize) {
            setError(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`)
            return
        }

        // Convert to base64 or create object URL
        const reader = new FileReader()
        reader.onload = (e) => {
            onChange(e.target.result)
        }
        reader.readAsDataURL(file)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragOver(false)

        const files = e.dataTransfer.files
        if (files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleClick = () => {
        if (!disabled) {
            fileInputRef.current?.click()
        }
    }

    const handleFileChange = (e) => {
        const files = e.target.files
        if (files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleRemove = (e) => {
        e.stopPropagation()
        onChange('')
        setError('')
    }

    return (
        <div className={`relative ${className}`}>
            <div
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`
                    relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                    ${isDragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    ${value ? 'border-green-300 bg-green-50' : ''}
                `}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={disabled}
                />

                {value ? (
                    <div className="relative">
                        <img
                            src={value}
                            alt="Uploaded"
                            className="max-w-full max-h-48 mx-auto rounded-lg"
                        />
                        {!disabled && (
                            <button
                                onClick={handleRemove}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto" />
                        <div className="text-sm text-gray-600">
                            {placeholder}
                        </div>
                        <div className="text-xs text-gray-500">
                            PNG, JPG, GIF up to {Math.round(maxSize / (1024 * 1024))}MB
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    )
}

export default ImageUpload
